/**
 * MessageThread — Gemini-style document layout.
 *
 *  - Centered 760px column
 *  - User messages: right-aligned pill
 *  - Assistant messages: flat markdown document
 *  - Streaming caret: blinking cursor at end of live text
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  ChatMessage,
  KubernetesMutationPreview,
  KubernetesMutationState,
  KubernetesToolData,
  ToolActivity,
  WebSearchToolData,
} from '../types';
import { renderMarkdown } from '../../chatbot/utils/markdownRenderer';
import { Lightbox } from './Attachments';
import { COLOR, FONT, ANIM } from '../styles/tokens';

// CC Spinner frames — braille dots
const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];

function ThinkingIndicator() {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setFrame(f => (f + 1) % SPINNER_FRAMES.length),
      80,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '6px 0 4px',
        fontFamily: FONT.mono,
        fontSize: FONT.size.sm,
        color: COLOR.textMuted,
        animation: 'nora-fadein 0.2s ease',
      }}
    >
      <span style={{ color: COLOR.blue }}>{SPINNER_FRAMES[frame]}</span>
      <span>Thinking...</span>
    </div>
  );
}

// ── Tool activity — one line per tool call (k8s lookups, web search) ─────────

function ActivitySpinner() {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setFrame(f => (f + 1) % SPINNER_FRAMES.length),
      80,
    );
    return () => clearInterval(id);
  }, []);
  return <span style={{ color: COLOR.blue }}>{SPINNER_FRAMES[frame]}</span>;
}

const KIND_SECTIONS: Record<string, string> = {
  Pod: 'pods',
  Deployment: 'deployments',
  StatefulSet: 'statefulsets',
  DaemonSet: 'daemonsets',
  Job: 'jobs',
  CronJob: 'cronjobs',
  Service: 'services',
  Ingress: 'ingresses',
  HTTPRoute: 'httproutes',
  PersistentVolumeClaim: 'persistentvolumeclaims',
  ConfigMap: 'configmaps',
  Certificate: 'certificates',
  Application: 'applications',
};

const EmbeddedResourceContext = createContext(false);

function safeExternalUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.href
      : null;
  } catch {
    return null;
  }
}

function resourceHref(
  cluster: string,
  namespace: string | undefined,
  kind: string,
  name: string,
): string {
  const params = new URLSearchParams({
    cluster,
    section: KIND_SECTIONS[kind] ?? `${kind.toLowerCase()}s`,
    kind,
    name,
  });
  if (namespace) params.set('namespace', namespace);
  return `/kubernetes?${params}`;
}

function ResourceLink({
  cluster,
  namespace,
  kind,
  name,
}: {
  cluster: string;
  namespace?: string;
  kind: string;
  name: string;
}) {
  const embedded = useContext(EmbeddedResourceContext);
  return (
    <a
      href={resourceHref(cluster, namespace, kind, name)}
      onClick={event => {
        if (!embedded || window.parent === window) return;
        event.preventDefault();
        window.parent.postMessage(
          {
            type: 'nora.resource.select',
            resource: { cluster, namespace, kind, name },
          },
          window.location.origin,
        );
      }}
      style={{ color: COLOR.blue, textDecoration: 'none', fontWeight: 600 }}
    >
      {name}
    </a>
  );
}

function formatMutationState(
  state: KubernetesMutationState | undefined,
): string {
  if (!state) return '—';
  const parts: string[] = [];
  if (state.replicas !== undefined) parts.push(`${state.replicas} replicas`);
  if (state.readyReplicas !== undefined)
    parts.push(`${state.readyReplicas} ready`);
  if (state.restartedAt) {
    parts.push(`restarted ${new Date(state.restartedAt).toLocaleString()}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

function KubernetesMutationResultCard({
  data,
}: {
  data: Extract<KubernetesToolData, { action: 'restart' | 'scale' }>;
}) {
  const cardStyle: React.CSSProperties = {
    margin: '7px 0 9px 21px',
    padding: '10px 12px',
    border: `1px solid ${COLOR.green}40`,
    borderLeft: `3px solid ${COLOR.green}`,
    borderRadius: 12,
    background: COLOR.bgRaised,
    color: COLOR.textPrimary,
    fontFamily: FONT.sans,
    fontSize: FONT.size.sm,
  };
  const muted: React.CSSProperties = {
    color: COLOR.textMuted,
    fontSize: FONT.size.xs,
  };
  return (
    <div style={cardStyle}>
      <div style={{ marginBottom: 6, fontWeight: 700 }}>
        {data.action === 'restart' ? 'Restarted rollout' : 'Scaled'} ·{' '}
        <ResourceLink
          cluster={data.cluster}
          namespace={data.namespace}
          kind={data.kind}
          name={data.name}
        />
      </div>
      <div style={{ ...muted, marginBottom: 8 }}>
        {data.cluster} · {data.namespace} · applied{' '}
        {new Date(data.appliedAt).toLocaleString()}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          <div style={muted}>Before</div>
          <div>{formatMutationState(data.before)}</div>
        </div>
        <div>
          <div style={muted}>After</div>
          <div>{formatMutationState(data.after)}</div>
        </div>
      </div>
    </div>
  );
}

function KubernetesResultCard({ data }: { data: KubernetesToolData }) {
  const cardStyle: React.CSSProperties = {
    margin: '7px 0 9px 21px',
    padding: '10px 12px',
    border: `1px solid ${COLOR.borderSubtle}`,
    borderRadius: 12,
    background: COLOR.bgRaised,
    color: COLOR.textPrimary,
    fontFamily: FONT.sans,
    fontSize: FONT.size.sm,
    overflowX: 'auto',
  };
  const muted: React.CSSProperties = {
    color: COLOR.textMuted,
    fontSize: FONT.size.xs,
  };

  if (data.tool === 'k8s_list_pods') {
    return (
      <div style={cardStyle}>
        <div style={{ marginBottom: 8, fontWeight: 700 }}>
          Pods in {data.namespace}{' '}
          <span style={muted}>
            · {data.cluster} · {data.count} found
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={muted}>
              {['Pod', 'Status', 'Ready', 'Restarts'].map(label => (
                <th
                  key={label}
                  style={{ padding: '4px 8px', textAlign: 'left' }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.pods.slice(0, 20).map(pod => (
              <tr key={pod.name}>
                <td style={{ padding: '4px 8px' }}>
                  <ResourceLink
                    cluster={data.cluster}
                    namespace={data.namespace}
                    kind="Pod"
                    name={pod.name}
                  />
                </td>
                <td style={{ padding: '4px 8px' }}>{pod.status}</td>
                <td style={{ padding: '4px 8px' }}>{pod.ready}</td>
                <td style={{ padding: '4px 8px' }}>{pod.restarts}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.pods.length > 20 && (
          <div style={{ ...muted, marginTop: 6 }}>
            Showing 20 of {data.pods.length} pods
          </div>
        )}
      </div>
    );
  }

  if (data.tool === 'k8s_diagnose_pod') {
    const severityColor =
      data.severity === 'critical'
        ? COLOR.red
        : data.severity === 'warning'
        ? '#f4c145'
        : COLOR.green;
    return (
      <div style={{ ...cardStyle, borderLeft: `3px solid ${severityColor}` }}>
        <div style={{ marginBottom: 7, fontWeight: 700 }}>
          <ResourceLink
            cluster={data.cluster}
            namespace={data.namespace}
            kind="Pod"
            name={data.pod}
          />{' '}
          <span style={{ color: severityColor }}>
            {data.phase ?? data.severity}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {data.containers.map(container => (
            <span
              key={container.name}
              style={{
                padding: '4px 7px',
                borderRadius: 7,
                background: COLOR.bgOverlay,
                color: container.ready ? COLOR.green : severityColor,
              }}
            >
              {container.name}: {container.ready ? 'ready' : 'not ready'}
              {container.waitingReason
                ? ` · ${container.waitingReason}`
                : container.terminatedReason
                ? ` · ${container.terminatedReason}`
                : ''}
              {container.restarts ? ` · ${container.restarts} restarts` : ''}
            </span>
          ))}
        </div>
        {data.events.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {data.events.slice(0, 3).map((event, index) => (
              <div
                key={`${event.reason}-${index}`}
                style={{ ...muted, marginTop: 3 }}
              >
                {event.type === 'Warning' ? 'Warning' : event.type}:{' '}
                {event.reason} — {event.message}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (data.tool === 'k8s_pod_logs') {
    return (
      <div style={cardStyle}>
        <div style={{ marginBottom: 7, fontWeight: 700 }}>
          Logs ·{' '}
          <ResourceLink
            cluster={data.cluster}
            namespace={data.namespace}
            kind="Pod"
            name={data.pod}
          />{' '}
          <span style={muted}>
            · {data.container} · {data.lineCount} lines
          </span>
        </div>
        <pre
          style={{
            maxHeight: 180,
            margin: 0,
            padding: 8,
            overflow: 'auto',
            borderRadius: 7,
            background: COLOR.bg,
            color: COLOR.textSecondary,
            whiteSpace: 'pre-wrap',
          }}
        >
          {data.logsPreview || '(no log output)'}
        </pre>
      </div>
    );
  }

  if (data.tool === 'k8s_restart_rollout' || data.tool === 'k8s_scale') {
    return <KubernetesMutationResultCard data={data} />;
  }

  const resource = data.resource;
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 700 }}>
        {resource?.kind && resource.name ? (
          <ResourceLink
            cluster={data.cluster}
            namespace={resource.namespace}
            kind={resource.kind}
            name={resource.name}
          />
        ) : (
          'Kubernetes resource result'
        )}
      </div>
      <div style={{ ...muted, marginTop: 4 }}>
        {resource?.kind ?? data.path}
        {resource?.phase ? ` · ${resource.phase}` : ''}
        {resource?.itemCount !== undefined
          ? ` · ${resource.itemCount} items`
          : ''}
      </div>
    </div>
  );
}

function WebSearchResultCard({ data }: { data: WebSearchToolData }) {
  return (
    <div
      style={{
        margin: '7px 0 9px 21px',
        padding: '10px 12px',
        border: `1px solid ${COLOR.borderSubtle}`,
        borderRadius: 12,
        background: COLOR.bgRaised,
        fontFamily: FONT.sans,
      }}
    >
      <strong style={{ color: COLOR.textPrimary }}>
        Web evidence for “{data.query}”
      </strong>
      <div style={{ display: 'grid', gap: 6, marginTop: 7 }}>
        {data.results.slice(0, 5).map((result, index) => {
          const href = safeExternalUrl(result.link);
          return href ? (
            <a
              key={result.link}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLOR.blue, fontSize: FONT.size.sm }}
            >
              [{index + 1}] {result.title}
            </a>
          ) : (
            <span
              key={`${result.title}-${index}`}
              style={{ color: COLOR.textMuted }}
            >
              [{index + 1}] {result.title}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// Preview-and-confirm card for a mutating Kubernetes tool call — shown from
// the moment the backend asks for approval (tool.permission) until the user
// approves/cancels. Renders only from the backend's own structured preview
// (SelfSubjectAccessReview result + a fresh GET of the target object) —
// never from free-text the model wrote — so what it shows is provably what
// will happen.
function KubernetesConfirmCard({
  entry,
  onApprove,
  onDeny,
}: {
  entry: ToolActivity;
  onApprove: () => void;
  onDeny: () => void;
}) {
  const confirm = entry.confirm;
  if (!confirm) return null;
  const preview = confirm.preview;
  const isPending = entry.status === 'pending';
  const isExecuting = entry.status === 'executing';
  const isDenied = entry.status === 'denied';
  const accent = confirm.isDestructive ? COLOR.red : COLOR.blue;

  return (
    <div
      style={{
        margin: '7px 0 9px 21px',
        padding: '12px 14px',
        border: `1px solid ${accent}40`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 12,
        background: COLOR.bgRaised,
        fontFamily: FONT.sans,
        fontSize: FONT.size.sm,
        color: COLOR.textPrimary,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        {preview ? (
          <>
            {preview.action === 'restart' ? 'Restart rollout' : 'Scale'} ·{' '}
            <ResourceLink
              cluster={preview.cluster}
              namespace={preview.namespace}
              kind={preview.kind}
              name={preview.name}
            />
          </>
        ) : (
          'Confirm Kubernetes action'
        )}
      </div>
      {preview && (
        <div
          style={{
            color: COLOR.textMuted,
            fontSize: FONT.size.xs,
            marginBottom: 8,
          }}
        >
          {preview.cluster} · {preview.namespace}
        </div>
      )}
      <div style={{ marginBottom: 8 }}>{confirm.message}</div>
      {preview && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div>
            <div style={{ color: COLOR.textMuted, fontSize: FONT.size.xs }}>
              Current
            </div>
            <div>{formatMutationState(preview.current)}</div>
          </div>
          <div>
            <div style={{ color: COLOR.textMuted, fontSize: FONT.size.xs }}>
              Proposed
            </div>
            <div>{formatMutationState(preview.proposed)}</div>
          </div>
        </div>
      )}
      {preview && (
        <div
          style={{
            color: COLOR.textMuted,
            fontSize: FONT.size.xs,
            marginBottom: 6,
          }}
        >
          RBAC: {preview.rbac.allowed ? 'allowed' : 'denied'} to{' '}
          {preview.rbac.verb} {preview.rbac.resource}
          {preview.rbac.subresource ? `/${preview.rbac.subresource}` : ''}
        </div>
      )}
      {preview && <div style={{ marginBottom: 6 }}>{preview.impact}</div>}
      {preview?.warnings.map(warning => (
        <div key={warning} style={{ color: COLOR.orange, marginBottom: 4 }}>
          ⚠ {warning}
        </div>
      ))}
      {isPending && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={onApprove}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: 'none',
              background: accent,
              color: '#0b1020',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Approve
          </button>
          <button
            type="button"
            onClick={onDeny}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: `1px solid ${COLOR.borderSubtle}`,
              background: 'transparent',
              color: COLOR.textPrimary,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}
      {isExecuting && (
        <div style={{ color: COLOR.textMuted, marginTop: 4 }}>Applying…</div>
      )}
      {isDenied && (
        <div style={{ color: COLOR.textMuted, marginTop: 4 }}>
          Cancelled — no change was made.
        </div>
      )}
    </div>
  );
}

function ToolDisclosure({ entry }: { entry: ToolActivity }) {
  if (!entry.input && !entry.kubernetes && !entry.webSearch) return null;
  return (
    <details
      style={{
        margin: '4px 0 8px 21px',
        color: COLOR.textMuted,
        fontFamily: FONT.sans,
        fontSize: FONT.size.xs,
      }}
    >
      <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
        Evidence and tool details
      </summary>
      <div
        style={{
          marginTop: 6,
          padding: 8,
          border: `1px solid ${COLOR.borderSubtle}`,
          borderRadius: 8,
          background: COLOR.bg,
        }}
      >
        <div>
          <strong>Tool:</strong> {entry.toolName}
        </div>
        {entry.input && (
          <div style={{ marginTop: 5 }}>
            <strong>Input:</strong>{' '}
            {Object.entries(entry.input)
              .map(([key, value]) => `${key}=${String(value)}`)
              .join(', ')}
          </div>
        )}
        {entry.kubernetes && (
          <div style={{ marginTop: 5 }}>
            <strong>Evidence:</strong> structured Kubernetes API result
          </div>
        )}
        {entry.webSearch && (
          <div style={{ marginTop: 5 }}>
            <strong>Evidence:</strong> {entry.webSearch.results.length} web
            sources
          </div>
        )}
      </div>
    </details>
  );
}

function EvidencePanel({ activity }: { activity: ToolActivity[] }) {
  const resources = activity.flatMap(entry => {
    const data = entry.kubernetes;
    if (!data) return [];
    if (data.tool === 'k8s_list_pods') {
      return data.pods.slice(0, 10).map(pod => ({
        key: `${data.cluster}/${data.namespace}/Pod/${pod.name}`,
        cluster: data.cluster,
        namespace: data.namespace,
        kind: 'Pod',
        name: pod.name,
      }));
    }
    if (data.tool === 'k8s_diagnose_pod' || data.tool === 'k8s_pod_logs') {
      return [
        {
          key: `${data.cluster}/${data.namespace}/Pod/${data.pod}`,
          cluster: data.cluster,
          namespace: data.namespace,
          kind: 'Pod',
          name: data.pod,
        },
      ];
    }
    if (data.tool === 'k8s_restart_rollout' || data.tool === 'k8s_scale') {
      return [
        {
          key: `${data.cluster}/${data.namespace}/${data.kind}/${data.name}`,
          cluster: data.cluster,
          namespace: data.namespace,
          kind: data.kind,
          name: data.name,
        },
      ];
    }
    const resource = data.resource;
    return resource?.kind && resource.name
      ? [
          {
            key: `${data.cluster}/${resource.namespace}/${resource.kind}/${resource.name}`,
            cluster: data.cluster,
            namespace: resource.namespace,
            kind: resource.kind,
            name: resource.name,
          },
        ]
      : [];
  });
  const uniqueResources = [
    ...new Map(resources.map(resource => [resource.key, resource])).values(),
  ];
  const webSources = activity.flatMap(entry => entry.webSearch?.results ?? []);
  if (!uniqueResources.length && !webSources.length) return null;
  return (
    <details
      style={{
        marginTop: 10,
        color: COLOR.textMuted,
        fontFamily: FONT.sans,
        fontSize: FONT.size.sm,
      }}
    >
      <summary style={{ cursor: 'pointer', color: COLOR.textSecondary }}>
        Sources and evidence ({uniqueResources.length + webSources.length})
      </summary>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {uniqueResources.map(resource => (
          <ResourceLink
            key={resource.key}
            cluster={resource.cluster}
            namespace={resource.namespace}
            kind={resource.kind}
            name={resource.name}
          />
        ))}
        {webSources.map((source, index) => {
          const href = safeExternalUrl(source.link);
          return href ? (
            <a
              key={source.link}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLOR.blue }}
            >
              [{index + 1}] {source.title}
            </a>
          ) : null;
        })}
      </div>
    </details>
  );
}

function ToolActivityList({
  activity,
  onPermissionDecision,
}: {
  activity: NonNullable<ChatMessage['activity']>;
  onPermissionDecision: (toolUseId: string, approved: boolean) => void;
}) {
  if (activity.length === 0) return null;
  return (
    <div style={{ margin: '2px 0 10px' }}>
      {activity.map(entry => (
        <React.Fragment key={entry.id}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '2px 0',
              fontFamily: FONT.mono,
              fontSize: FONT.size.sm,
              color: COLOR.textMuted,
            }}
          >
            {entry.status === 'running' || entry.status === 'executing' ? (
              <ActivitySpinner />
            ) : entry.status === 'pending' ? (
              <span style={{ color: COLOR.orange }}>⏳</span>
            ) : entry.status === 'denied' ? (
              <span style={{ color: COLOR.textMuted }}>✕</span>
            ) : (
              <span
                style={{
                  color: entry.status === 'error' ? COLOR.red : COLOR.green,
                }}
              >
                {entry.status === 'error' ? '✕' : '✓'}
              </span>
            )}
            <span>{entry.label}</span>
          </div>
          {entry.confirm &&
            (entry.status === 'pending' ||
              entry.status === 'executing' ||
              entry.status === 'denied') && (
              <KubernetesConfirmCard
                entry={entry}
                onApprove={() => onPermissionDecision(entry.id, true)}
                onDeny={() => onPermissionDecision(entry.id, false)}
              />
            )}
          {entry.kubernetes && <KubernetesResultCard data={entry.kubernetes} />}
          {entry.webSearch && <WebSearchResultCard data={entry.webSearch} />}
          <ToolDisclosure entry={entry} />
        </React.Fragment>
      ))}
    </div>
  );
}

// ── User message — Gemini-style right-aligned pill ───────────────────────────

function UserMessage({
  msg,
  isMobile,
  onImageClick,
  disabled,
  onEdit,
  onRetry,
  onBranch,
}: {
  msg: ChatMessage;
  isMobile: boolean;
  onImageClick: (src: string) => void;
  disabled: boolean;
  onEdit: (message: ChatMessage, text: string) => void;
  onRetry: (message: ChatMessage) => void;
  onBranch: (message: ChatMessage) => void;
}) {
  const images = msg.images ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.text);
  return (
    <div
      style={{
        display: 'grid',
        justifyItems: 'end',
        margin: '20px 0 24px',
      }}
    >
      <div
        style={{
          maxWidth: isMobile ? '85%' : '70%',
          background: COLOR.bgOverlay,
          borderRadius: 20,
          padding: '10px 18px',
          fontSize: FONT.size.md,
          lineHeight: FONT.lineHeight.relaxed,
          color: COLOR.textPrimary,
          fontFamily: FONT.sans,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {images.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              marginBottom: msg.text ? 8 : 0,
              justifyContent: 'flex-end',
            }}
          >
            {images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`attachment ${i + 1}`}
                onClick={() => onImageClick(src)}
                style={{
                  maxWidth: images.length === 1 ? 320 : 156,
                  maxHeight: images.length === 1 ? 260 : 156,
                  borderRadius: 10,
                  border: `1px solid ${COLOR.borderSubtle}`,
                  cursor: 'zoom-in',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ))}
          </div>
        )}
        {editing ? (
          <textarea
            autoFocus
            value={draft}
            onChange={event => setDraft(event.target.value)}
            rows={Math.min(8, Math.max(2, draft.split('\n').length))}
            style={{
              width: 'min(520px, 70vw)',
              boxSizing: 'border-box',
              resize: 'vertical',
              border: `1px solid ${COLOR.border}`,
              borderRadius: 8,
              outline: 'none',
              background: COLOR.bg,
              color: COLOR.textPrimary,
              font: 'inherit',
            }}
          />
        ) : (
          msg.text
        )}
      </div>
      <div
        aria-label="Message actions"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginTop: 3,
          opacity: disabled ? 0.45 : 0.75,
        }}
      >
        {editing ? (
          <>
            <button
              type="button"
              disabled={disabled || !draft.trim()}
              onClick={() => onEdit(msg, draft.trim())}
              style={messageAction}
            >
              Send edit
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(msg.text);
                setEditing(false);
              }}
              style={messageAction}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setEditing(true)}
              style={messageAction}
              title="Edit into a new branch"
            >
              Edit
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onRetry(msg)}
              style={messageAction}
              title="Retry in a new branch"
            >
              Retry
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onBranch(msg)}
              style={messageAction}
              title="Branch through this message"
            >
              Branch
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const messageAction: React.CSSProperties = {
  padding: '3px 6px',
  border: 0,
  background: 'transparent',
  color: COLOR.textMuted,
  cursor: 'pointer',
  fontFamily: FONT.sans,
  fontSize: FONT.size.xs,
};

// ── Thread ────────────────────────────────────────────────────────────────────

interface Props {
  messages: ChatMessage[];
  streaming: boolean;
  error: string | null;
  isMobile: boolean;
  embedded?: boolean;
  loading?: boolean;
  onPermissionDecision: (toolUseId: string, approved: boolean) => void;
  onBranchMessage: (
    message: ChatMessage,
    options: {
      message?: string;
      retry: boolean;
      continueWith?: string;
      throughMessage?: ChatMessage;
    },
  ) => void;
}

export function MessageThread({
  messages,
  streaming,
  error,
  isMobile,
  embedded = false,
  loading = false,
  onPermissionDecision,
  onBranchMessage,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  if (messages.length === 0) return null;

  const lastIdx = messages.length - 1;

  return (
    <EmbeddedResourceContext.Provider value={embedded}>
      <div
        className="nora-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: isMobile ? '8px 14px 4px' : '16px 28px 8px',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {messages.map((msg, idx) =>
            msg.role === 'user' ? (
              <UserMessage
                key={msg.id}
                msg={msg}
                isMobile={isMobile}
                onImageClick={setLightbox}
                disabled={streaming || loading}
                onEdit={(message, text) =>
                  onBranchMessage(message, {
                    message: text,
                    retry: false,
                    continueWith: text,
                    throughMessage: idx > 0 ? messages[idx - 1] : undefined,
                  })
                }
                onRetry={message =>
                  onBranchMessage(message, {
                    retry: true,
                    continueWith: message.text,
                    throughMessage: idx > 0 ? messages[idx - 1] : undefined,
                  })
                }
                onBranch={message =>
                  onBranchMessage(message, {
                    retry: false,
                    throughMessage: message,
                  })
                }
              />
            ) : (
              <div
                key={msg.id}
                className="nora-prose"
                style={{
                  marginBottom: 20,
                  padding: '4px 0',
                  fontSize: FONT.size.md,
                  lineHeight: FONT.lineHeight.relaxed,
                  color: COLOR.textPrimary,
                  fontFamily: FONT.sans,
                }}
              >
                {msg.activity && (
                  <ToolActivityList
                    activity={msg.activity}
                    onPermissionDecision={onPermissionDecision}
                  />
                )}
                {/* eslint-disable-next-line react/no-danger */}
                <span
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }}
                />
                {msg.activity && <EvidencePanel activity={msg.activity} />}
                {idx === lastIdx && streaming && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 7,
                      height: '0.85em',
                      background: COLOR.textSecondary,
                      marginLeft: 2,
                      verticalAlign: 'text-bottom',
                      animation: ANIM.blink,
                    }}
                  />
                )}
              </div>
            ),
          )}

          {streaming && messages[lastIdx]?.role === 'user' && (
            <ThinkingIndicator />
          )}

          {error && (
            <div
              style={{
                margin: '8px 0',
                padding: '10px 14px',
                background: `${COLOR.red}10`,
                border: `1px solid ${COLOR.red}40`,
                borderLeft: `3px solid ${COLOR.red}`,
                borderRadius: '0 6px 6px 0',
                fontFamily: FONT.sans,
                fontSize: FONT.size.sm,
                color: COLOR.red,
              }}
            >
              <strong>Error: </strong>
              {error}
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </EmbeddedResourceContext.Provider>
  );
}
